/**
 * DriversSettingsPage — carrier admin driver roster
 * /settings/drivers?carrierId=<orgId>
 *
 * Shows all DRIVER-role users in the carrier org with:
 *  - TMS profile (CDL class, expiry dates, compliance warnings)
 *  - Equipment assignment (combo / tractor+trailer)
 * Links to per-driver edit page.
 */
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { settingsApi, type DriverProfileEntry } from '../lib/api'
import { useAuth } from '../lib/auth-context'

function ComplianceCell({ warnings }: { warnings?: string[] }) {
  if (!warnings || warnings.length === 0) {
    return <span className="text-green-700 text-xs font-medium">✓ OK</span>
  }
  return (
    <div className="space-y-0.5">
      {warnings.map((w, i) => (
        <div key={i} className={`text-xs font-medium ${w.includes('EXPIRED') ? 'text-red-700' : 'text-amber-700'}`}>
          ⚠ {w}
        </div>
      ))}
    </div>
  )
}

function EquipmentCell({ entry }: { entry: DriverProfileEntry }) {
  if (!entry.equipment) {
    return <span className="text-xs text-gray-400 italic">Not assigned</span>
  }
  const { tractorManufacturer, tractorModel, trailerManufacturer, trailerModel, unitLabel } = entry.equipment
  return (
    <div className="text-xs">
      {unitLabel && <div className="font-semibold text-blue-800 mb-0.5">{unitLabel}</div>}
      {tractorManufacturer && <div className="text-gray-700">{tractorManufacturer} {tractorModel}</div>}
      {trailerManufacturer && <div className="text-gray-500">{trailerManufacturer} {trailerModel}</div>}
    </div>
  )
}

export default function DriversSettingsPage() {
  const { user, hasRole } = useAuth()
  const [params] = useSearchParams()

  // carrierId from query param, fallback to caller's own orgId
  const carrierId = params.get('carrierId') || user?.orgId || ''

  const isAdminLike = hasRole('ADMIN', 'AUX_ADMIN', 'SUPER_ADMIN', 'CARRIER_ADMIN', 'DISPATCHER')

  const driversQ = useQuery({
    queryKey: ['driver-profiles', carrierId],
    queryFn: () => settingsApi.listDriverProfiles(carrierId).then(r => r.data),
    enabled: !!carrierId && isAdminLike,
  })

  const canEdit = hasRole('ADMIN', 'AUX_ADMIN', 'SUPER_ADMIN', 'CARRIER_ADMIN')

  if (!carrierId) {
    return (
      <div className="p-6 text-gray-500 text-sm">
        No carrier org found. Your account may not be linked to a carrier.
      </div>
    )
  }

  if (!isAdminLike) {
    return <div className="p-6 text-red-600 text-sm">Access denied.</div>
  }

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Driver Roster</h1>
          <p className="text-sm text-gray-500 mt-1">Carrier ID: <span className="font-mono">{carrierId}</span></p>
        </div>
        <Link to="/settings" className="text-sm text-blue-600 hover:text-blue-800">← Settings</Link>
      </div>

      {driversQ.isLoading && (
        <div className="text-gray-400 text-sm py-8 text-center">Loading drivers…</div>
      )}

      {driversQ.error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">
          Failed to load drivers. {(driversQ.error as any)?.message}
        </div>
      )}

      {driversQ.data && driversQ.data.drivers.length === 0 && (
        <div className="text-gray-400 text-sm py-8 text-center italic">
          No drivers found in this carrier org.
          <br />
          Drivers must sign in once to appear here.
        </div>
      )}

      {driversQ.data && driversQ.data.drivers.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Driver</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">CDL</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Compliance</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Equipment</th>
                {canEdit && <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {driversQ.data.drivers.map(entry => (
                <tr key={entry.auxUserId} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{entry.name ?? 'Unnamed'}</div>
                    <div className="text-xs text-gray-400">{entry.email}</div>
                    {entry.profile?.unitLabel && (
                      <div className="text-xs text-blue-700 mt-0.5">{entry.profile.unitLabel}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {entry.profile?.cdlClass
                      ? <span className="inline-block bg-gray-100 text-gray-800 text-xs font-semibold px-2 py-0.5 rounded">CDL-{entry.profile.cdlClass}</span>
                      : <span className="text-gray-400 text-xs italic">Not set</span>}
                    {entry.profile?.licenseState && (
                      <span className="ml-1 text-xs text-gray-500">{entry.profile.licenseState}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <ComplianceCell warnings={entry.profile?.complianceWarnings} />
                  </td>
                  <td className="px-4 py-3">
                    <EquipmentCell entry={entry} />
                  </td>
                  {canEdit && (
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/settings/drivers/${entry.auxUserId}?carrierId=${carrierId}`}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Edit Profile →
                      </Link>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-gray-400 mt-4">
        Only users with the DRIVER role appear here. Contact your AUX admin to add driver accounts.
      </p>
    </div>
  )
}
